import React, { useRef, useState } from "react";
import { Typography, Chip, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

const BLUE     = "#3B82F6";
const BLUE_DIM = "rgba(59,130,246,0.08)";
const BORDER   = "#dde0ee";
const SURFACE  = "#f5f6fa";
const TEXT     = "#1a1d2e";
const MUTED    = "#7b80a0";
const RED      = "#e05c5c";
const RED_DIM  = "rgba(224,92,92,0.08)";
const RED_BORDER = "rgba(224,92,92,0.3)";

const FORMATS         = ["PDF", "DOCX"];
const ACCEPTED_TYPES  = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const ACCEPTED_EXTS   = [".pdf", ".docx"];
const MAX_SIZE_BYTES  = 10 * 1024 * 1024; // 10 MB

function validateFiles(fileList) {
  const valid   = [];
  const errors  = [];

  Array.from(fileList).forEach((file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    const validType = ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTS.includes(ext);
    const validSize = file.size <= MAX_SIZE_BYTES;

    if (!validType) {
      errors.push(`"${file.name}" is not a supported file type.`);
    } else if (!validSize) {
      errors.push(`"${file.name}" exceeds the 10 MB limit.`);
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}

const useStyles = makeStyles(() => ({
  root: {
    border: `1.5px dashed ${BORDER}`,
    borderRadius: 10,
    padding: "22px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    background: SURFACE,
    transition: "border-color 0.2s, background 0.2s",
    textAlign: "center",
    "&:hover": { borderColor: BLUE, background: BLUE_DIM },
  },
  active:     { borderColor: BLUE, background: BLUE_DIM },
  uploadIcon: { color: BLUE, fontSize: 28 },
  title:      { fontSize: 12, fontWeight: 500, color: TEXT },
  browse:     { color: BLUE },
  sub:        { fontSize: 11, color: MUTED },
  badge: {
    fontSize: 10,
    height: 20,
    background: "#fff",
    border: `0.5px solid ${BORDER}`,
    color: `${MUTED} !important`,
    borderRadius: 4,
    "& .MuiChip-label": { padding: "0 7px" },
  },
  errorBox: {
    marginTop: 8,
    background: RED_DIM,
    border: `0.5px solid ${RED_BORDER}`,
    borderRadius: 6,
    padding: "8px 11px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  errorRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
  },
  errorIcon: { color: RED, fontSize: 14, marginTop: 1, flexShrink: 0 },
  errorText: { fontSize: 11, color: RED, lineHeight: 1.5 },
}));

export default function DropZone({ onFilesAdded }) {
  const cls = useStyles();
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [errors,   setErrors]   = useState([]);

  const process = (fileList) => {
    const { valid, errors: errs } = validateFiles(fileList);
    setErrors(errs);
    if (valid.length > 0) onFilesAdded(valid);
    // reset input so the same file can be re-selected after fixing errors
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    process(e.dataTransfer.files);
  };

  return (
    <>
      <div
        className={`${cls.root} ${dragging ? cls.active : ""}`}
        onClick={() => inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <CloudUploadOutlinedIcon className={cls.uploadIcon} />
        <Typography className={cls.title}>
          Drop files here or <span className={cls.browse}>browse</span>
        </Typography>
        <Typography className={cls.sub}>
          AI will analyze and extract context per module
        </Typography>
        <Box display="flex" gridGap={5} mt={0.5}>
          {FORMATS.map((f) => (
            <Chip key={f} label={f} size="small" className={cls.badge} />
          ))}
          <Chip label="Max 10 MB" size="small" className={cls.badge} />
        </Box>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx"
        style={{ display: "none" }}
        onChange={(e) => process(e.target.files)}
      />

      {errors.length > 0 && (
        <div className={cls.errorBox}>
          {errors.map((msg, i) => (
            <div key={i} className={cls.errorRow}>
              <ErrorOutlineIcon className={cls.errorIcon} />
              <Typography className={cls.errorText}>{msg}</Typography>
            </div>
          ))}
        </div>
      )}
    </>
  );
}